import { useRef, useState } from 'react';
import { normalizeScan } from '@civilos/domain';
import { api } from '../shared/api.ts';
import { useAuth } from '../shared/auth.tsx';
import { fmtDateTime } from '../shared/format.ts';
import { Badge, Card, Empty, ErrorBox, Field, Notice, useAction, useLoad } from '../shared/ui.tsx';

/**
 * Office custody scan station (CUST-001/002/003). A USB/Bluetooth scanner
 * acts as a keyboard: each scan types the paper number followed by Enter.
 */
export function CustodyStation() {
  const { me } = useAuth();
  const meta = useLoad(() => api.get('/api/meta'));
  const transfers = useLoad(() => api.get('/api/custody/transfers'));
  const [action, setAction] = useState<'issue_to_server' | 'return_to_office'>('issue_to_server');
  const [serverId, setServerId] = useState('');
  const mode = 'grab_a_stack'; // deputies choose Route or Freelance on their own screen
  const [queue, setQueue] = useState<{ n: string; scanned: boolean }[]>([]);
  const [input, setInput] = useState('');
  const [results, setResults] = useState<any[] | null>(null);
  const typed = useRef(0);
  const a = useAction();

  const add = (raw: string) => {
    const n = normalizeScan(raw);
    if (!n) return;
    // Scanners deliver a burst of keystrokes; slow typing is recorded as manual entry.
    const scanned = typed.current > 0 && Date.now() - typed.current < 800;
    setQueue((q) => (q.some((x) => x.n === n) ? q : [...q, { n, scanned }]));
    setInput('');
    typed.current = 0;
  };

  const submit = () =>
    a.run(async () => {
      const r = await api.post('/api/custody/scan', {
        action,
        serverId: action === 'issue_to_server' ? serverId : undefined,
        mode,
        paperNumbers: queue.map((q) => q.n),
        scanned: queue.every((q) => q.scanned),
      });
      setResults(r.results);
      setQueue([]);
    });

  return (
    <div className="stack">
      <h1>Custody scan station</h1>
      {me!.agency.custodyScanRequired && <Notice>This agency requires barcode scans for custody changes. Manually typed numbers need supervisor permission.</Notice>}
      <Card>
        <div className="grid2">
          <Field label="Action">
            <select value={action} onChange={(e) => setAction(e.target.value as typeof action)}>
              <option value="issue_to_server">Issue to server (assigns)</option>
              <option value="return_to_office">Return to office</option>
            </select>
          </Field>
          {action === 'issue_to_server' && (
            <>
              <Field label="Process server">
                <select value={serverId} onChange={(e) => setServerId(e.target.value)} required>
                  <option value="">Select…</option>
                  {meta.data?.servers.map((s: any) => (
                    <option key={s.id} value={s.id}>
                      {s.display_name} {s.badge_number ? `(${s.badge_number})` : ''}
                    </option>
                  ))}
                </select>
              </Field>
            </>
          )}
        </div>
        <form
          className="row gap"
          onSubmit={(e) => {
            e.preventDefault();
            add(input);
          }}
        >
          <input
            className="grow scan-input"
            value={input}
            placeholder="Scan barcode or type paper number, then Enter"
            autoFocus
            onChange={(e) => {
              if (!typed.current) typed.current = Date.now();
              setInput(e.target.value);
            }}
          />
          <button className="btn">Add</button>
        </form>
        {queue.length > 0 && (
          <div className="stack sm">
            <div className="chips">
              {queue.map((q) => (
                <span key={q.n} className="chip">
                  <span className="mono">{q.n}</span> {!q.scanned && <Badge tone="warn">typed</Badge>}
                  <button className="x" aria-label={`Remove ${q.n}`} onClick={() => setQueue(queue.filter((x) => x.n !== q.n))}>
                    ×
                  </button>
                </span>
              ))}
            </div>
            <div className="row gap">
              <button className="btn primary" onClick={submit} disabled={a.busy || (action === 'issue_to_server' && !serverId)}>
                {action === 'issue_to_server' ? `Issue ${queue.length} paper(s)` : `Return ${queue.length} paper(s) to office`}
              </button>
              <button className="btn ghost" onClick={() => setQueue([])}>
                Clear
              </button>
            </div>
          </div>
        )}
        <ErrorBox error={a.error} />
      </Card>
      {results && (
        <Card title="Last batch">
          <ul className="plain">
            {results.map((r) => (
              <li key={r.paperNumber} className="row gap">
                <span className="mono">{r.paperNumber}</span> {r.ok ? <Badge tone="ok">{r.status}</Badge> : <Badge tone="bad">{r.error}</Badge>}
              </li>
            ))}
          </ul>
        </Card>
      )}
      <Card title="Pending field transfers">
        {!transfers.data?.transfers.length ? (
          <Empty>No pending transfers.</Empty>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Paper</th>
                <th>From → To</th>
                <th>Status</th>
                <th>Authorized</th>
              </tr>
            </thead>
            <tbody>
              {transfers.data.transfers.map((t: any) => (
                <tr key={t.id}>
                  <td className="mono">{t.civilos_number}</td>
                  <td>
                    {t.from_name} → {t.to_name}
                  </td>
                  <td>{t.status.replace('_', ' ')}</td>
                  <td>{fmtDateTime(t.authorized_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
